import React from "react";
import PropTypes from "prop-types";
import {useController} from "react-hook-form";
import {Autocomplete, Grid, Checkbox, TextField} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FormGuide from "./FormGuide";
import UpgradeAdornment from "../UpgradeAdornment";
import ItemTooltip from "./ItemTooltip";
import SelectTagChip from "../chips/SelectTagChip";
import {useCustomSelectStyles} from "./formsStyles";
import AgentFieldHighlight from "./AgentFieldHighlight";
import {find, omit, reject, some} from "underscore";

const CustomSelect = ({
  name,
  label,
  guide,
  required,
  needUpgrade,
  multiple,
  defaultValue,
  disabled,
  disableCloseOnSelect,
  options,
  optionLabel,
  optionChipColor,
  onSelect,
  conditionallyDisableOption,
  hideErrors,
  errorText,
  hideSelect,
  size,
  isControlled = false,
  value,
  onChange,
  noSpacingTop,
  agentHighlight = false,
  transform = {
    to: (obj) => obj,
    from: (obj) => obj,
  },
}) => {
  if (hideSelect) {
    return null;
  }
  const {classes} = useCustomSelectStyles({needUpgrade: needUpgrade});
  const optionalDefaultValue = transform.to(defaultValue ? defaultValue : multiple ? [] : null);
  let controller;
  try {
    controller = useController({
      name,
      defaultValue: optionalDefaultValue,
      rules: {required: required},
    });
  } catch (e) {
    if (!isControlled) {
      throw e;
    }
  }
  const isError = Boolean(!hideErrors && isControlled ? false : controller?.fieldState?.error);
  const whichLabel = optionLabel || "title";
  const itemWidth = guide ? 8 : 12;
  const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const ALL = {id: 0, value: "ALL", name: "ALL", [whichLabel]: `All - ${label}`};

  const handleChange = (_, data, onChange) => {
    const finalData = transform.to(data?.[0]?.id === ALL.id ? options : data);
    onChange(finalData);
    onSelect && onSelect(finalData);
  };

  const getInputProps = () => {
    if (isControlled) {
      return {};
    }
    return controller?.field;
  };

  const getOverrideValuesIfOptionConditionallyDisabled = (option) => {
    let overrideBackgroundColor;
    let overrideColor;
    let overrideTooltip;
    if (
      conditionallyDisableOption?.element &&
      option?.[conditionallyDisableOption?.element] === conditionallyDisableOption?.value
    ) {
      overrideBackgroundColor = conditionallyDisableOption?.backgroundColor;
      overrideColor = conditionallyDisableOption?.color;
      overrideTooltip = conditionallyDisableOption?.tooltip;
    }
    return {overrideBackgroundColor, overrideColor, overrideTooltip};
  };

  const shouldHideIfOptionConditionallyDisabled = (option, currentSelection) => {
    if (
      multiple &&
      some(currentSelection, (selected) => selected[whichLabel] === option[whichLabel])
    ) {
      return false;
    }
    if (!multiple && currentSelection?.[whichLabel] === option[whichLabel]) {
      return false;
    }

    return (
      conditionallyDisableOption?.element &&
      option?.[conditionallyDisableOption?.element] === conditionallyDisableOption?.value
    );
  };

  if (needUpgrade) {
    return (
      <Grid container spacing={1} className={classes.spaceTop}>
        <Grid item xs={itemWidth}>
          <TextField
            id={name}
            name={name}
            disabled
            className={classes.primary}
            label={label}
            variant="outlined"
            InputProps={needUpgrade ? {endAdornment: <UpgradeAdornment item={label} />} : undefined}
            style={{width: "100%"}}
            size={size}
          />
        </Grid>
        <FormGuide guide={guide} required={required && !needUpgrade} />
      </Grid>
    );
  }

  return (
    <AgentFieldHighlight active={agentHighlight}>
      <Grid
        container
        spacing={1}
        className={noSpacingTop ? undefined : classes.spaceTop}
        data-tour={`custom-select-${name}`}
      >
        <Grid item xs={itemWidth}>
          <Autocomplete
            {...getInputProps()}
            id={name}
            name={name}
            value={
              isControlled ?
                transform.from(value) :
                transform.from(controller?.field.value) ?? transform.from(optionalDefaultValue)
            }
            multiple={multiple}
            disabled={disabled || needUpgrade}
            disableCloseOnSelect={multiple || disableCloseOnSelect}
            options={reject(
              multiple &&
              (isControlled ?
                transform.from(value)?.length < 1 :
                transform.from(controller?.field?.value)?.length < 1) ?
                [ALL, ...transform.from(options)] :
                transform.from(options),
              (option) =>
                shouldHideIfOptionConditionallyDisabled(
                  option,
                  isControlled ? transform.from(value) : transform.from(controller?.field.value),
                ),
            )}
            getOptionLabel={(option) => option[whichLabel] || ""}
            isOptionEqualToValue={(option, value) => {
              if (option && value) {
                return option[whichLabel] === value[whichLabel];
              }
              return false;
            }}
            className={classes.primary}
            onChange={(_, data) =>
              handleChange(_, data, isControlled ? onChange : controller?.field.onChange)}
            renderOption={
              multiple ?
                (props, option, {selected}) => {
                  const {overrideBackgroundColor, overrideColor, overrideTooltip} =
                    getOverrideValuesIfOptionConditionallyDisabled(option);
                    // eslint-disable-next-line no-unused-vars
                  const {key, onClick, ...otherProps} = props;
                  const shouldDisableOption = !selected && !!overrideTooltip;
                  const finalProps = shouldDisableOption ? otherProps : props;
                  return (
                    <li
                      key={key}
                      {...omit(finalProps, "key")}
                      style={{
                        backgroundColor: overrideBackgroundColor,
                        color: overrideColor,
                      }}
                    >
                      <Checkbox
                        icon={uncheckedIcon}
                        checkedIcon={checkedIcon}
                        style={{marginRight: 8}}
                        checked={selected}
                        color="primary"
                        disabled={shouldDisableOption}
                      />
                      <ItemTooltip
                        item={
                          overrideTooltip ? {...option, description: overrideTooltip} : option
                        }
                        name={option[whichLabel]}
                        options={options}
                      >
                        {option[whichLabel]}
                      </ItemTooltip>
                    </li>
                  );
                } :
                (props, option) => {
                  const {overrideBackgroundColor, overrideColor, overrideTooltip} =
                    getOverrideValuesIfOptionConditionallyDisabled(option);
                  return (
                    <li
                      key={props?.key}
                      {...omit(props, "key")}
                      style={{backgroundColor: overrideBackgroundColor, color: overrideColor}}
                    >
                      <ItemTooltip
                        item={
                          overrideTooltip ? {...option, description: overrideTooltip} : option
                        }
                        name={option[whichLabel]}
                        options={transform.from(options)}
                      >
                        {option?.isLocal ? <b>{option[whichLabel]}</b> : option[whichLabel]}
                      </ItemTooltip>
                    </li>
                  );
                }
            }
            renderInput={(params) => {
              const currentValue = isControlled ?
                transform.from(value) :
                transform.from(controller?.field?.value);
              const overrideBackgroundColor = currentValue ?
                getOverrideValuesIfOptionConditionallyDisabled(currentValue)
                  .overrideBackgroundColor :
                undefined;
              return (
                <TextField
                  {...params}
                  name={name}
                  error={isError}
                  helperText={isError && errorText}
                  label={label}
                  variant="outlined"
                  size={size}
                  data-testid={name?.replaceAll(" ", "_")}
                  sx={
                    overrideBackgroundColor ? {input: {color: overrideBackgroundColor}} : undefined
                  }
                />
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const {overrideBackgroundColor, overrideColor, overrideTooltip} =
                  getOverrideValuesIfOptionConditionallyDisabled(option);
                const {key, ...rest} = getTagProps({index});
                const fallbackDescription = !option?.description ?
                  find(
                    transform.from(options),
                    (o) => o?.value === option[whichLabel] || o?.name === option[whichLabel],
                  )?.description :
                  undefined;
                return (
                  <SelectTagChip
                    key={key}
                    option={option}
                    optionLabel={whichLabel}
                    optionChipColor={optionChipColor}
                    deleteColorMode="foreground"
                    fallbackDescription={fallbackDescription}
                    tagProps={rest}
                    overrides={
                      overrideBackgroundColor || overrideColor || overrideTooltip ?
                        {
                          backgroundColor: overrideBackgroundColor,
                          color: overrideColor,
                          deleteColor: overrideColor,
                          tooltip: overrideTooltip,
                        } :
                        undefined
                    }
                  />
                );
              })}
          />
        </Grid>
        <FormGuide guide={guide} required={required} disabled={disabled} />
      </Grid>
    </AgentFieldHighlight>
  );
};

CustomSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  guide: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  required: PropTypes.bool,
  needUpgrade: PropTypes.bool,
  multiple: PropTypes.bool,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  options: PropTypes.array.isRequired,
  optionLabel: PropTypes.string,
  optionChipColor: PropTypes.string,
  onSelect: PropTypes.func,
  conditionallyDisableOption: PropTypes.object,
  hideErrors: PropTypes.bool,
  errorText: PropTypes.string,
  hideSelect: PropTypes.bool,
  size: PropTypes.string,
  isControlled: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func,
  noSpacingTop: PropTypes.bool,
  agentHighlight: PropTypes.bool,
  transform: PropTypes.shape({
    to: PropTypes.func.isRequired,
    from: PropTypes.func.isRequired,
  }),
};

export default CustomSelect;
